/**
 * The limits on a `shape:` object (ADR-030), in one place.
 *
 * The schema states the ones a schema can (lesson.schema.json; shape-limits.test.ts
 * holds the two together), validate.ts the ones it cannot -- a total across
 * nested groups, a count across objects -- and the lesson generator tells a
 * model all of them.
 */
export const SHAPE_LIMITS = {
    /** Parts in one shape object, counting groups and everything inside them. */
    partsPerObject: 64,
    /** Levels of parts: the object's own, a group's, a group-in-a-group's. */
    depth: 3,
    /** Points in one lathe outline or extrusion outline. */
    pointsPerProfile: 64,
    shapesPerLesson: 30,
    /** Each glow colour is a material and a draw call of its own (render/shapes.ts). */
    glowColoursPerShape: 4,
    glowColoursPerLesson: 8,
    /** Metres, for every size and extrusion depth. */
    sizeMin: 0.005,
    sizeMax: 10,
};
/**
 * Generator v3 (docs/superpowers/specs/2026-10-05-generator-v3-design.md):
 * the parts a shape keeps to in a lesson, well under the schema's
 * partsPerObject. A v2 run drew a volcano in hundreds of parts: slow to
 * write, to check and to repair, and no clearer than 20.
 */
export const SHAPE_PARTS_CAP = 40;
/** What a v3 prompt's catalogue says about it: the limit and its reason. */
export const SHAPE_PARTS_CAP_LINE = `In a generated lesson a shape has at most ${SHAPE_PARTS_CAP} parts, groups and what is in them counted: 5–20 make most things recognisable, and more is slow to write and to repair and no clearer in a headset. Make it simpler rather than bigger.`;
/** Every `type` a part can have. */
export const SHAPE_PART_TYPES = [
    'box',
    'sphere',
    'cylinder',
    'cone',
    'torus',
    'capsule',
    'plane',
    'lathe',
    'extrude',
    'group',
];
/** Whether `kind` is drawn from its own parts rather than by a builder of its own. */
export function isShapeKind(kind) {
    return kind.startsWith('shape:');
}
/** Deeper than any lesson means anything by; stops a hostile answer before the stack does. */
const WALK_LIMIT = 16;
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isSize = (value) => typeof value === 'number' && value >= SHAPE_LIMITS.sizeMin && value <= SHAPE_LIMITS.sizeMax;
/**
 * Measures a shape's `parts` as it stands, trusting nothing about it: the
 * lesson generator's checks read answers that may not have passed the schema,
 * and name what is wrong in words rather than as a schema path.
 */
export function measureParts(parts) {
    const measure = {
        count: 0,
        depth: 0,
        unknownTypes: [],
        longProfiles: 0,
        sizesOutOfRange: 0,
        glowColours: [],
    };
    const glow = new Set();
    const known = new Set(SHAPE_PART_TYPES);
    const unknown = new Set();
    const walk = (list, level) => {
        if (!Array.isArray(list) || list.length === 0 || level > WALK_LIMIT)
            return;
        measure.depth = Math.max(measure.depth, level);
        for (const part of list) {
            measure.count++;
            if (!isRecord(part)) {
                unknown.add('?');
                continue;
            }
            const type = part['type'];
            if (typeof type !== 'string' || !known.has(type)) {
                unknown.add(typeof type === 'string' ? type : '?');
            }
            const size = part['size'];
            if (Array.isArray(size)) {
                measure.sizesOutOfRange += size.filter((n) => !isSize(n)).length;
            }
            if (type === 'extrude' && !isSize(part['depth']))
                measure.sizesOutOfRange++;
            if (part['finish'] === 'glow' && typeof part['color'] === 'string') {
                glow.add(part['color'].toLowerCase());
            }
            for (const outline of [part['points'], part['outline']]) {
                if (Array.isArray(outline) && outline.length > SHAPE_LIMITS.pointsPerProfile) {
                    measure.longProfiles++;
                }
            }
            if (type === 'group')
                walk(part['parts'], level + 1);
        }
    };
    walk(parts, 1);
    measure.unknownTypes = [...unknown];
    measure.glowColours = [...glow];
    return measure;
}
/**
 * Every shape object of `lesson` with more than `cap` parts, counted as
 * measureParts counts them. Read from the raw document, trusting nothing,
 * since the generator's checks read answers that may not pass the schema;
 * `lessons:check` holds every stored lesson to it too.
 */
export function oversizedShapes(lesson, cap = SHAPE_PARTS_CAP) {
    if (!isRecord(lesson))
        return [];
    const objects = lesson['objects'];
    if (!isRecord(objects))
        return [];
    return Object.entries(objects).flatMap(([object, value]) => {
        if (!isRecord(value))
            return [];
        const kind = value['kind'];
        if (typeof kind !== 'string' || !isShapeKind(kind))
            return [];
        const parts = measureParts(value['parts']).count;
        return parts > cap ? [{ object, parts }] : [];
    });
}
//# sourceMappingURL=shape-limits.js.map